import styled from 'styled-components';
import Title from '@/components/Title/Title';
import { ITitle } from '@/components/BuilderIO/common/CommonFields';
import RichText from '@/components/RichText/RichText';
import { getComponentMargin } from '@/utils/builderUtils';
import beTwMerge from '@/utils/beTwMerge';

export interface ICell {
  content?: string;
  fontWeight?: 'Avenir Book' | 'Avenir Medium' | 'Avenir Heavy';
  cellColor?: 'White' | 'Grey 50' | 'Grey 100';
  horizontalAlignment?: 'left' | 'center';
  verticalAlignment?: 'top' | 'center';
  columnWidth?: number;
  minColumnWidth?: number;
}
export interface IColumnHeader {
  cell: ICell;
}

export interface IRow {
  cells: ICell[];
}
export interface IXYChart {
  title?: ITitle;
  description?: string;
  columnHeaders?: IColumnHeader[];
  rows?: IRow[];
  margin?: boolean;
}

const TABLE = styled.table`
  border: 1px solid #d9d9d9;
  display: block;
  overflow-x: auto;
  border-collapse: collapse;
  table-layout: fixed;

  @media (max-width: 767px) {
    border-width: 1.5px;
  }

  thead tr th {
    &:first-child {
      border-left: 0;
    }
    &:last-child {
      border-right: 0;
    }
    border-top: 0;
  }
  td,
  th {
    border: 1px solid #d9d9d9;
  }
  tbody tr {
    th {
      border-left: 0;
    }
    td:last-child {
      border-right: 0;
    }
    &:last-child {
      th,
      td {
        border-bottom: 0;
      }
    }
  }
`;

const THBODY = styled.th<{ $fontWeight?: string; $bgColor?: string; $width?: number }>`
  padding: 0;
  background-color: ${props => `${props.$bgColor} !important` || 'transparent'};
  word-break: break-word;
  padding: 8px;

  p,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ol li,
  ul li {
    font-family: ${props => props.$fontWeight || 'Avenir'};
    font-size: 12px;
    line-height: 16.39px;
    text-wrap: wrap;
  }

  a {
    padding: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 0;
  }

  @media screen and (min-width: 1024px) {
    padding: 12px;

    p,
    a,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ol li,
    ul li {
      font-size: 14px;
      line-height: 19.12px;
    }

    a {
      padding: 0;
    }
  }
`;

const TD = styled.td<{ $fontWeight?: string; $bgColor?: string; $width?: number }>`
  padding: 0;
  background-color: ${props => props.$bgColor || 'transparent'};
  word-break: break-word;
  padding: 8px;

  p,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ol li,
  ul li {
    font-family: ${props => props.$fontWeight || 'Avenir'};
    font-size: 12px;
    line-height: 16.39px;
    text-wrap: wrap;
  }

  a {
    padding: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 0;
  }

  @media screen and (min-width: 1024px) {
    padding: 12px;

    p,
    a,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ol li,
    ul li {
      font-size: 14px;
      line-height: 19.12px;
    }

    a {
      padding: 0;
    }
  }
`;

const TH = styled.th<{
  $width?: number;
  $minWidth?: number;
  $textAlign?: string;
  $bgColor?: string;
}>`
  background-color: ${props => `${props.$bgColor} !important` || 'transparent'};
  min-width: ${props => `${props.$minWidth ? `${props.$minWidth}px` : 'auto'}`};
  padding: 8px;

  p,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ol li,
  ul li {
    font-family: 'Avenir Heavy';
    font-size: 12px;
    font-weight: 800;
    text-align: ${props => `${props.$textAlign ? `${props.$textAlign}` : 'center'}`};
    line-height: 16.39px;
  }

  a {
    padding: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 0;
  }

  @media screen and (min-width: 1024px) {
    padding: 12px;
    width: ${props => `${props.$width ? `${props.$width}px` : 'auto'}`};

    p,
    a,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ol li,
    ul li {
      font-size: 14px;
      line-height: 19.12px;
    }

    a {
      padding: 0;
    }
  }
`;

const COLORS = {
  White: '#FFFFFF',
  'Grey 50': '#F9F9F9',
  'Grey 100': '#D9D9D9',
};

export default function XYChart({ title, description, columnHeaders = [], rows = [], margin }: IXYChart) {
  return (
    <div
      className={beTwMerge(
        'wrapper tw-w-full tw-flex tw-flex-col tw-items-center tw-max-w-[970px] tw-self-center lg:tw-self-auto lg:tw-max-w-none tw-px-[1rem] lg:tw-mx-[2rem] 2xl:tw-mx-auto lg:tw-px-0 lg:tw-w-auto',
        getComponentMargin(margin)
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-[8px] tw-max-w-[600px] lg:tw-max-w-[800px]">
        {title?.content && (
          <Title
            component={title.titleHTMLTag || 'h2'}
            weight={title.titleWeight || 'normal'}
            size={title.titleSize || 'large'}
            alignment="center"
            className="!tw-mb-0 tw-text-[28px] tw-leading-[38.25px] md:!tw-leading-[43.71px]"
          >
            {title.content}
          </Title>
        )}
        {description && (
          <RichText
            content={description}
            className="tw-text-center [&>p]:tw-font-[Avenir] [&>p]:tw-leading-[19.12px] [&>p]:tw-text-black"
          />
        )}
      </div>
      {columnHeaders.length > 0 && (
        <div className="tw-flex tw-w-full tw-justify-center tw-mt-[24px] tw-overflow-x-auto lg:tw-mt-[2rem]">
          <TABLE>
            <thead>
              <tr>
                {columnHeaders.map((columnHeader: IColumnHeader, index) => (
                  <TH
                    tabIndex={index}
                    $width={columnHeader.cell.columnWidth}
                    $minWidth={columnHeader?.cell?.minColumnWidth}
                    $textAlign={columnHeader.cell.horizontalAlignment}
                    $bgColor={COLORS[columnHeader?.cell?.cellColor || '']}
                  >
                    <RichText content={columnHeader.cell.content || ''} />
                  </TH>
                ))}
              </tr>
            </thead>
            {rows.length > 0 && (
              <tbody>
                {rows.map((row: IRow) => (
                  <tr>
                    {row?.cells?.map((cell: any, cellIndex: number) => {
                      if (cellIndex === 0) {
                        return (
                          <THBODY
                            className={beTwMerge(
                              `tw-text-${cell?.cell?.horizontalAlignment || 'left'} tw-align-${cell?.cell?.verticalAlignment || 'top'}`
                            )}
                            tabIndex={cellIndex}
                            $fontWeight={cell?.cell?.fontWeight}
                            $bgColor={COLORS[cell?.cell?.cellColor]}
                          >
                            <RichText content={cell?.cell?.content} />
                          </THBODY>
                        );
                      }

                      return (
                        <TD
                          className={beTwMerge(
                            `tw-text-${cell?.cell?.horizontalAlignment || 'left'} tw-align-${cell?.cell?.verticalAlignment || 'top'}`
                          )}
                          tabIndex={cellIndex}
                          $fontWeight={cell?.cell?.fontWeight}
                          $bgColor={COLORS[cell?.cell?.cellColor]}
                        >
                          <RichText content={cell?.cell?.content} deep={2} />
                        </TD>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            )}
          </TABLE>
        </div>
      )}
    </div>
  );
}
